import React, { useState, useRef, useContext, useEffect, useCallback } from "react";
import { PermissionsContext } from "../../utils/PermissionsContext";
import {
  Box,
  Grid,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Card,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailIcon from "@mui/icons-material/Email";
import PrintIcon from "@mui/icons-material/Print";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import AddIcon from "@mui/icons-material/Add";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import Tooltip from "@mui/material/Tooltip";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useNavigate, useParams, useLocation } from "react-router-dom";

// Shared components
import CommentsTab from "./components/CommentsTab";
import CommentModal from "./components/CommentModal";
import WorkOrderTimeline from "./components/WorkOrderTimeline";
import MemberProfileCard from "./components/MemberProfileCard";
import DetailsTab from "./components/DetailsTab";
import CalendarTab from "./components/CalendarTab";
import VisitsTab from "./components/VisitsTab";
import ImageModal from "./components/ImageModal";
import WorkOrderPrintContent from "./components/WorkOrderPrintContent";
import AddWorkOrderSmart from "../MemberProfilePage/addWorkorder";
import TimeLogsTab from "./components/TimeLogsTab";
import MaterialsTab from "./components/MaterialsTab";
import WOStatusHistoryTab from "./components/WOStatusHistoryTab";
import WOLinksTab from "./components/WOLinksTab";
import WOClockInOutTab from "./components/WOClockInOutTab";
import "./components/WorkOrderPrintStyles.css";

// Hooks and API
import useWorkOrderDetails from "./hooks/useWorkOrderDetails";
import api, { API_BASE_URL } from "../../utils/api";

const WorkOrdersDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const printRef = useRef();
  const location = useLocation();

  const permissions = useContext(PermissionsContext);
  const hasPermission = (perm) => permissions?.includes(perm);

  const storedUser = sessionStorage.getItem("user");
  let currentUserId = Number(localStorage.getItem("userId")) || null;

  try {
    if (!currentUserId && storedUser) {
      const parsed = JSON.parse(storedUser);
      currentUserId = Number(parsed?.userId) || null;
    }
  } catch (err) {
    console.error("❌ Failed to parse user from localStorage:", err);
  }

  const {
    workOrderDetails,
    member,
    setMember,
    comments,
    statuses,
    users,
    priorityLevels,
    assignableUsers,
    workOrderTypes,
    workOrderSubtypes,
    workOrderLocations,
    selectedTypeId,
    selectedSubtypeId,
    selectedLocationId,
    selectedPriority,
    selectedAssignee,
    setSelectedAssignee,
    setWorkOrderDetails,
    fetchComments,
    handleTypeChange,
    handlePriorityChange,
    handleSubtypeChange,
    handleLocationChange,
    calculateDaysOpen,
    imageURL,
    attachedFiles,
    isLoading,
    error,
    commentsError,
    filesError,
    mutationError,
  } = useWorkOrderDetails(id, currentUserId);

  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [commentType, setCommentType] = useState("external");
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [ccEmail, setCcEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [commentImageURL, setCommentImageURL] = useState(null);
  const [openCommentImageModal, setOpenCommentImageModal] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [activeTab, setActiveTab] = useState(0);
  const [statusHistory, setStatusHistory] = useState([]);
  const [woLinks, setWOLinks] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [editingComment, setEditingComment] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [historyRecords, setHistoryRecords] = useState([]);
  const [unresolvedTickets, setUnresolvedTickets] = useState([]);
  const [availableProperties, setAvailableProperties] = useState([]);
  const [openNewTicketModal, setOpenNewTicketModal] = useState(false);
  const [materialsEnabled, setMaterialsEnabled] = useState(false);

  // Billing state
  const [billingStatus,      setBillingStatus]      = useState(null);
  const [billingGenerating,  setBillingGenerating]  = useState(false);
  // Manual price dialog. shown when no billing rule exists or rule has no fixed rate
  const [billingDialog, setBillingDialog] = useState({
    open: false, laborPrice: "0.00", materialsPrice: "0.00",
    timeCode: "", materialsCode: "", message: "",
  });

  // Surface hook-level errors through the shared Snackbar
  useEffect(() => {
    const msg = commentsError || filesError || mutationError;
    if (msg) {
      setSnackbarMessage(msg);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [commentsError, filesError, mutationError]);

  // Fetch work order feature flags
  useEffect(() => {
    api.get("/work-order-features")
      .then((res) => setMaterialsEnabled(res.data?.enable_materials_billing === true))
      .catch(() => setMaterialsEnabled(false));
  }, []);

  // Fetch billing status for this work order
  const fetchBillingStatus = () => {
    if (!id) return;
    api.get(`/work-orders/${id}/billing-status`)
      .then((res) => setBillingStatus(res.data))
      .catch(() => setBillingStatus(null));
  };
  useEffect(() => { fetchBillingStatus(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGenerateBilling = async () => {
    setBillingGenerating(true);
    try {
      const res = await api.post(`/work-orders/${id}/generate-billing`);

      if (res.data?.needs_manual_input) {
        // No rule / no fixed rate. open the price dialog pre-filled with suggestions
        setBillingDialog({
          open:           true,
          laborPrice:     (res.data.suggested_labor    ?? 0).toFixed(2),
          materialsPrice: (res.data.suggested_materials ?? 0).toFixed(2),
          timeCode:       res.data.time_billing_code       ?? "",
          materialsCode:  res.data.materials_billing_code  ?? "",
          message:        res.data.message || "No billing rule found. Enter amounts manually.",
        });
      } else {
        const verb = res.data?.updated ? "updated" : "created";
        setSnackbarMessage(`✅ Billing record ${verb}, labor: $${(res.data.labor_price ?? 0).toFixed(2)}, materials: $${(res.data.materials_price ?? 0).toFixed(2)}`);
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        fetchBillingStatus();
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to generate billing record";
      setSnackbarMessage(`❌ ${msg}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setBillingGenerating(false);
    }
  };

  const handleBillingDialogConfirm = async () => {
    setBillingGenerating(true);
    try {
      const res = await api.post(`/work-orders/${id}/generate-billing`, {
        override_labor:          parseFloat(billingDialog.laborPrice)     || 0,
        override_materials:      parseFloat(billingDialog.materialsPrice) || 0,
        override_time_code:      billingDialog.timeCode      || undefined,
        override_materials_code: billingDialog.materialsCode || undefined,
      });
      const verb = res.data?.updated ? "updated" : "created";
      setSnackbarMessage(`✅ Billing record ${verb}, labor: $${(res.data.labor_price ?? 0).toFixed(2)}, materials: $${(res.data.materials_price ?? 0).toFixed(2)}`);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      fetchBillingStatus();
      setBillingDialog(d => ({ ...d, open: false }));
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to save billing record";
      setSnackbarMessage(`❌ ${msg}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setBillingGenerating(false);
    }
  };

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  const dialogPaperSx = {
    borderRadius: "18px",
    background: "rgba(255,255,255,0.98)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
  };

  const textFieldSx = {
    mt: 1.25,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  useEffect(() => {
    const fetchUnresolved = async () => {
      if (!workOrderDetails?.property_id) return;

      try {
        const memberWorkOrdersResp = await api.get(
          `/workorders/member/${workOrderDetails.property_id}`
        );

        const unresolvedData = (memberWorkOrdersResp.data || []).filter(
          (wo) => wo.status_id !== 4
        );

        setUnresolvedTickets(unresolvedData);
      } catch (err) {
        console.error("❌ Failed to fetch unresolved tickets:", err);
        setUnresolvedTickets([]);
      }
    };

    fetchUnresolved();
  }, [workOrderDetails?.property_id, member]);

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const res = await api.get("/membersfilter");
        setAvailableProperties(res.data.members || []);
      } catch (err) {
        console.error("❌ Failed to fetch members:", err);
        setAvailableProperties([]);
      }
    };

    fetchMembers();
  }, []);

  const handlePropertyChange = async (event) => {
    const newPropertyId = event.target.value;

    try {
      await api.put(`/editWorkOrder/${workOrderDetails.order_id}`, {
        property_id: newPropertyId,
        assign_to: workOrderDetails.assign_to,
        status: workOrderDetails.status,
        priority: workOrderDetails.priority,
      });

      setWorkOrderDetails((prev) => ({
        ...prev,
        property_id: newPropertyId,
      }));

      setSnackbarMessage("✅ Property updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (err) {
      console.error("❌ Failed to update property:", err);
      setSnackbarMessage("❌ Failed to update property!");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handlePropertyChanged = async (newPropertyId) => {
    try {
      await api.get(`/workorder/${id}`);
      await fetchComments();

      const memberWorkOrdersResp = await api.get(
        `/workorders/member/${newPropertyId}`
      );
      const unresolvedData = (memberWorkOrdersResp.data || []).filter(
        (wo) => wo.status_id !== 4
      );
      setUnresolvedTickets(unresolvedData);

      try {
        setMember?.(null);
        const memberResponse = await api.get(`/members/${newPropertyId}`);
        if (memberResponse.data) {
          setMember?.(memberResponse.data);
        }
      } catch (memberErr) {
        console.error("⚠️ Failed to refresh member info:", memberErr);
      }

      setWorkOrderDetails((prev) => ({
        ...prev,
        property_id: newPropertyId,
      }));

      setSnackbarMessage("✅ Refreshed data for new property");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (err) {
      console.error("❌ Failed to refresh after property change:", err);
      setSnackbarMessage("❌ Failed to refresh data");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleAssigneesChange = async (ids) => {
    if (typeof setSelectedAssignee === "function") {
      setSelectedAssignee(ids);
    } else {
      setWorkOrderDetails((prev) => ({
        ...prev,
        assignees: ids,
        assign_to: ids[0] || null,
      }));
    }

    if (!workOrderDetails?.order_id) {
      setSnackbarMessage("❌ Missing work order ID.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    try {
      await api.put("/assignWorkOrder", {
        order_id: workOrderDetails.order_id,
        assignee_ids: ids,
        primary_assignee_id: ids[0] || null,
        assigned_by: Number(currentUserId) || null,
        role: "Collaborator",
      });

      setWorkOrderDetails((prev) => ({
        ...prev,
        assignees: ids,
        assign_to: ids[0] || null,
      }));

      setSnackbarMessage("✅ Assignees updated!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (err) {
      console.error("assignWorkOrder failed", err);
      setSnackbarMessage("❌ Failed to update assignees.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleEditCommentClick = (comment) => {
    setEditingComment(comment);
    setIsEditModalOpen(true);
  };

  const handleViewHistoryClick = async (commentOrId) => {
    const comment_id =
      typeof commentOrId === "object" ? commentOrId.comment_id : commentOrId;

    if (!comment_id) return;

    try {
      const response = await api.get(`/workorder-comment-history/${comment_id}`);
      setHistoryRecords(response.data || []);
      setIsHistoryModalOpen(true);
    } catch (err) {
      console.error("Failed to fetch comment history:", err);
      setSnackbarMessage("❌ Failed to load comment history.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      alert("Comment cannot be empty.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("work_order_id", id);
      formData.append("user_id", currentUserId);
      formData.append("comment_text", newComment);
      formData.append("is_internal", commentType === "internal" ? "1" : "0");

      const response = await api.post("/addComment", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const newCommentId = response.data?.comment_id || response.data?.id;

      if (!newCommentId) {
        alert("Failed to create comment (no comment_id returned).");
        return;
      }

      if (selectedFiles?.length > 0) {
        const filesData = new FormData();
        filesData.append("workorder_id", id);
        filesData.append("uploaded_by", currentUserId);
        filesData.append("comment_id", newCommentId);

        selectedFiles.forEach((file) => filesData.append("upload_urls", file));

        await api.post("/workorder-files", filesData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      await fetchComments();
      setNewComment("");
      setCommentType("internal");
      setSelectedFiles([]);
      setSelectedFile(null);
      setFilePreview(null);
      setIsCommentModalOpen(false);
      setSnackbarMessage("✅ Comment added successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Error adding comment:", err.response?.data || err.message);
      setSnackbarMessage("❌ Failed to add comment.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handlePrintWorkOrder = () => {
    const printContents = printRef.current.innerHTML;
    const printWindow = window.open("", "_blank", "width=960,height=900");
    if (!printWindow) return;
    printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>Work Order #${workOrderDetails?.order_id ?? ""}</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html, body { background: #fff; font-family: 'Segoe UI', Arial, sans-serif; color: #1a202c; }
    img { max-width: 100%; display: block; }
    table { border-collapse: collapse; }
    @media print {
      html, body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
      .no-print { display: none !important; }
    }
    .print-btn-bar { position: fixed; top: 0; right: 0; z-index: 9999; padding: 10px 16px; background: rgba(255,255,255,0.95); border-bottom-left-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.15); display: flex; gap: 8px; }
    .print-btn { background: #2f63ad; color: #fff; border: none; padding: 8px 20px; border-radius: 6px; font-size: 14px; font-weight: 600; cursor: pointer; font-family: inherit; }
    .print-btn:hover { background: #1e3a5f; }
    .close-btn { background: #6b7280; color: #fff; border: none; padding: 8px 16px; border-radius: 6px; font-size: 14px; cursor: pointer; font-family: inherit; }
  </style>
</head>
<body>
  <div class="print-btn-bar no-print">
    <button class="print-btn" onclick="window.print()">🖨 Print / Save as PDF</button>
    <button class="close-btn" onclick="window.close()">Close</button>
  </div>
  <div style="padding-top:56px">${printContents}</div>
  <script>
    var imgs = document.images, loaded = 0, total = imgs.length;
    function tryPrint() { if (++loaded >= total) setTimeout(function(){ window.print(); }, 400); }
    if (total === 0) setTimeout(function(){ window.print(); }, 400);
    else for (var i = 0; i < total; i++) { if (imgs[i].complete) tryPrint(); else { imgs[i].onload = tryPrint; imgs[i].onerror = tryPrint; } }
  </script>
</body>
</html>`);
    printWindow.document.close();
    printWindow.focus();
  };

  const handleSendEmail = async () => {
    setIsSending(true);
    try {
      await api.post("/send-workorder-email", {
        workOrderId: id,
        recipientEmail,
        ccEmail: ccEmail || null,
      });
      setSnackbarMessage("✅ Email sent successfully.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setEmailDialogOpen(false);
    } catch (err) {
      setSnackbarMessage("❌ Failed to send email.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      console.error("Send email error:", err);
    } finally {
      setIsSending(false);
    }
  };

  const handleTabChange = (_, val) => {
    setActiveTab(val);
    if (val === 2) fetchComments();
  };

  const handleNewTicketCreated = async () => {
    setOpenNewTicketModal(false);

    try {
      if (workOrderDetails?.property_id) {
        const memberWorkOrdersResp = await api.get(
          `/workorders/member/${workOrderDetails.property_id}`
        );

        const unresolvedData = (memberWorkOrdersResp.data || []).filter(
          (wo) => wo.status_id !== 4
        );

        setUnresolvedTickets(unresolvedData);
      }

      setSnackbarMessage("✅ New ticket created successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Failed to refresh tickets after creation:", err);
      setSnackbarMessage("✅ Ticket created, but failed to refresh list.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: "40vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" sx={{ textAlign: "center", mt: 4 }}>
        {error}
      </Typography>
    );
  }

  const selectedAssigneePrimary = Array.isArray(selectedAssignee)
    ? (selectedAssignee[0] ?? null)
    : selectedAssignee;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Box sx={{ mb: 2.5 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() =>
              navigate(location.state?.from || "/home/workorders", {
                state: {
                  reload: true,
                  tabIndex: location.state?.tabIndex,
                },
              })
            }
            sx={secondaryButtonSx}
          >
            Back
          </Button>
        </Box>

        <Box sx={{ mb: 3.5 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "#223142",
              mb: 0.5,
            }}
          >
            Work Order Details
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "#6b778c",
            }}
          >
            Review work order details, scheduling activity, comments, visits, and create related tickets.
          </Typography>
        </Box>

        <Grid container spacing={2.5}>
          {member && (
            <Grid item xs={12} lg={4}>
              <Card sx={{ ...pageCardSx, p: 2 }}>
                <MemberProfileCard
                  member={member}
                  unresolvedTickets={unresolvedTickets.filter(
                    (t) => String(t.order_id) !== String(id)
                  )}
                />
              </Card>

              <Card sx={{ ...pageCardSx, p: 2, mt: 2.5 }}>
                <WorkOrderTimeline workOrderId={workOrderDetails?.order_id} />
              </Card>
            </Grid>
          )}

          <Grid item xs={12} lg={8}>
            <Card
              sx={{
                ...pageCardSx,
                overflow: "hidden",
                borderTop: "4px solid #2f63ad",
              }}
            >
              <Box
                sx={{
                  px: { xs: 1.5, sm: 2, md: 3 },
                  pt: 2.25,
                  pb: 1.5,
                  borderBottom: "1px solid rgba(47,99,173,0.08)",
                  backgroundColor: "rgba(47,99,173,0.02)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", md: "center" },
                    gap: 2,
                    flexDirection: { xs: "column", md: "row" },
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.75,
                      }}
                    >
                      <AssignmentOutlinedIcon sx={{ color: "#2f63ad" }} />
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, color: "#223142" }}
                      >
                        Work Order #{workOrderDetails?.order_id}
                      </Typography>
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{
                        color: "#6b778c",
                      }}
                    >
                      {workOrderDetails?.status_name ||
                        workOrderDetails?.status ||
                        "Work Order"}
                      {selectedPriority ? ` • Priority ${selectedPriority}` : ""}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.25,
                      flexWrap: "wrap",
                      width: { xs: "100%", md: "auto" },
                    }}
                  >
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setOpenNewTicketModal(true)}
                      sx={primaryButtonSx}
                    >
                      New Ticket
                    </Button>

                    <Button
                      variant="contained"
                      startIcon={<EmailIcon />}
                      onClick={() => setEmailDialogOpen(true)}
                      sx={primaryButtonSx}
                    >
                      Send via Email
                    </Button>

                    {hasPermission("manage_billing_settings") && (
                      <Tooltip
                        title={
                          billingStatus?.has_pending_record
                            ? "A pending billing record already exists. review it in Integrations"
                            : billingStatus?.has_billing_record
                            ? "Previously billed. click to generate a new record"
                            : "Generate a billing record for review in Integrations"
                        }
                      >
                        <span>
                          <Button
                            variant="outlined"
                            startIcon={billingGenerating ? <CircularProgress size={14} /> : <ReceiptLongIcon />}
                            onClick={handleGenerateBilling}
                            disabled={billingGenerating || billingStatus?.has_pending_record}
                            sx={{
                              ...secondaryButtonSx,
                              borderColor: billingStatus?.has_pending_record ? "#f59e0b" : "#2f63ad",
                              color:       billingStatus?.has_pending_record ? "#f59e0b" : "#2f63ad",
                            }}
                          >
                            {billingStatus?.has_pending_record ? "Billing Pending" : "Generate Billing"}
                          </Button>
                        </span>
                      </Tooltip>
                    )}

                    <Button
                      variant="outlined"
                      startIcon={<PrintIcon />}
                      onClick={handlePrintWorkOrder}
                      sx={secondaryButtonSx}
                    >
                      Print
                    </Button>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{
                    mb: 3,
                    minHeight: "auto",
                    "& .MuiTab-root": {
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "10px",
                      minHeight: 40,
                      color: "#5f6b7a",
                      mr: 1,
                      px: { xs: 1.5, md: 2 },
                      py: 1,
                      whiteSpace: "nowrap",
                      minWidth: "fit-content",
                    },
                    "& .Mui-selected": {
                      color: "#2f63ad !important",
                      backgroundColor: "rgba(47,99,173,0.08)",
                    },
                    "& .MuiTabs-indicator": {
                      display: "none",
                    },
                  }}
                >
                  <Tab label="Details" />
                  <Tab label="Calendar" />
                  <Tab label="Comments" />
                  <Tab label="Visits" />
                  <Tab label="Time Logs" />
                  <Tab label="Clock In/Out" />
                  <Tab label="Materials" sx={{ display: materialsEnabled ? "flex" : "none" }} />
                  <Tab label="History" />
                  <Tab label="Links" />
                </Tabs>

                <Divider sx={{ mb: 3, borderColor: "rgba(47,99,173,0.08)" }} />

                {workOrderDetails?.notify_member === 0 || workOrderDetails?.notify_member === false ? (
                  <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                    <strong>Member opted out of notifications.</strong> This member selected not to be contacted. No emails will be sent for status changes or comments, even if you add one manually.
                  </Alert>
                ) : null}

                {activeTab === 0 && (
                  <DetailsTab
                    workOrderDetails={workOrderDetails}
                    statuses={statuses}
                    assignableUsers={assignableUsers}
                    workOrderTypes={workOrderTypes}
                    workOrderSubtypes={workOrderSubtypes}
                    workOrderLocations={workOrderLocations}
                    selectedTypeId={selectedTypeId}
                    selectedSubtypeId={selectedSubtypeId}
                    selectedLocationId={selectedLocationId}
                    selectedPriority={selectedPriority}
                    selectedAssignee={workOrderDetails?.assignees || []}
                    priorityLevels={priorityLevels}
                    onTypeChange={handleTypeChange}
                    onSubtypeChange={handleSubtypeChange}
                    onLocationChange={handleLocationChange}
                    onAssigneesChange={handleAssigneesChange}
                    onPriorityChange={handlePriorityChange}
                    calculateDaysOpen={calculateDaysOpen}
                    setSnackbarMessage={setSnackbarMessage}
                    setSnackbarOpen={setSnackbarOpen}
                    setWorkOrderDetails={setWorkOrderDetails}
                    imageURL={imageURL}
                    availableProperties={availableProperties}
                    handlePropertyChange={handlePropertyChange}
                    onPropertyChanged={handlePropertyChanged}
                  />
                )}

                {activeTab === 1 && (
                  <CalendarTab
                    workOrderId={id}
                    currentUserId={currentUserId}
                    events={[]}
                    users={users}
                    setEvents={() => {}}
                  />
                )}

                {activeTab === 2 && (
                  <CommentsTab
                    comments={comments}
                    onAddCommentClick={() => setIsCommentModalOpen(true)}
                    onEditCommentClick={handleEditCommentClick}
                    onViewHistoryClick={handleViewHistoryClick}
                    onImageClick={(url) => {
                      setCommentImageURL(url);
                      setOpenCommentImageModal(true);
                    }}
                    apiBaseUrl={API_BASE_URL}
                    userPermissions={permissions}
                    notifyMember={workOrderDetails?.notify_member}
                  />
                )}

                {activeTab === 3 && (
                  <VisitsTab work_order_id={id} currentUserId={currentUserId} />
                )}
             {activeTab === 4 && (
  <TimeLogsTab
    workOrderId={id}
    currentUserId={currentUserId}
  />
)}

                {activeTab === 5 && (
                  <WOClockInOutTab workOrderId={id} currentUserId={currentUserId} />
                )}

                {materialsEnabled && activeTab === 6 && (
                  <MaterialsTab
                    workOrderId={id}
                    canEdit={true}
                  />
                )}

                {/* Status History tab */}
                {activeTab === 7 && (
                  <WOStatusHistoryTab workOrderId={id} />
                )}

                {/* Links tab */}
                {activeTab === 8 && (
                  <WOLinksTab workOrderId={id} />
                )}

              </Box>
            </Card>
          </Grid>
        </Grid>

        <CommentModal
          open={isCommentModalOpen}
          onClose={() => setIsCommentModalOpen(false)}
          commentType={commentType}
          setCommentType={setCommentType}
          newComment={newComment}
          setNewComment={setNewComment}
          selectedFile={selectedFile}
          setSelectedFile={setSelectedFile}
          filePreview={filePreview}
          setFilePreview={setFilePreview}
          onSave={handleAddComment}
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
        />

        <CommentModal
          open={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          commentType={editingComment?.is_internal ? "internal" : "external"}
          newComment={editingComment?.comment_text || ""}
          setNewComment={(text) =>
            setEditingComment((prev) => ({ ...prev, comment_text: text }))
          }
          onSave={async () => {
            try {
              await api.put(`/editComment/${editingComment.comment_id}`, {
                comment_text: editingComment.comment_text,
                edited_by: currentUserId,
              });
              await fetchComments();
              setSnackbarMessage("✅ Comment updated successfully!");
              setSnackbarSeverity("success");
            } catch (err) {
              console.error("Edit failed", err);
              setSnackbarMessage("❌ Failed to update comment.");
              setSnackbarSeverity("error");
            } finally {
              setSnackbarOpen(true);
              setIsEditModalOpen(false);
            }
          }}
          selectedFile={null}
          setSelectedFile={() => {}}
          filePreview={null}
          setFilePreview={() => {}}
          selectedFiles={[]}
          setSelectedFiles={() => {}}
        />

        <Dialog
          open={isHistoryModalOpen}
          onClose={() => setIsHistoryModalOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: dialogPaperSx }}
        >
          <DialogTitle sx={{ fontWeight: 700, color: "#223142" }}>
            Comment History
          </DialogTitle>
          <DialogContent dividers>
            {historyRecords.length > 0 ? (
              historyRecords.map((record, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 2,
                    p: 2,
                    border: "1px solid #ddd",
                    borderRadius: "12px",
                    backgroundColor: "#fafafa",
                  }}
                >
                  <Typography variant="subtitle2" sx={{ color: "#1976d2" }}>
                    ✏️ Edited By:{" "}
                    <strong>
                      {`${record.edited_by_name || "Unknown"} ${
                        record.edited_by_lastname || ""
                      }`}
                    </strong>
                  </Typography>

                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <strong>Edited On:</strong>{" "}
                    {record.edited_at
                      ? new Date(record.edited_at).toLocaleString()
                      : "N/A"}
                  </Typography>

                  <Box sx={{ mt: 1.5 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: "bold", color: "#b71c1c" }}
                    >
                      Old Comment:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: "pre-wrap",
                        backgroundColor: "#ffe6e6",
                        borderRadius: "6px",
                        p: 1,
                        mt: 0.5,
                      }}
                    >
                      {record.old_text || "(empty)"}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: "bold", mt: 1, color: "#1b5e20" }}
                    >
                      New Comment:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: "pre-wrap",
                        backgroundColor: "#e8f5e9",
                        borderRadius: "6px",
                        p: 1,
                        mt: 0.5,
                      }}
                    >
                      {record.new_text || "(empty)"}
                    </Typography>
                  </Box>
                </Box>
              ))
            ) : (
              <Typography>No history found for this comment.</Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={() => setIsHistoryModalOpen(false)}
              sx={secondaryButtonSx}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <ImageModal
          open={openCommentImageModal}
          imageURL={commentImageURL}
          onClose={() => setOpenCommentImageModal(false)}
        />

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            variant="filled"
            sx={{ width: "100%", borderRadius: 3 }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>

        <div style={{ display: "none" }}>
          <div ref={printRef}>
            <WorkOrderPrintContent
              workOrderDetails={workOrderDetails}
              selectedAssignee={selectedAssigneePrimary}
              selectedPriority={selectedPriority}
              selectedTypeId={selectedTypeId}
              selectedSubtypeId={selectedSubtypeId}
              selectedLocationId={selectedLocationId}
              workOrderTypes={workOrderTypes}
              workOrderSubtypes={workOrderSubtypes}
              workOrderLocations={workOrderLocations}
              comments={comments}
              member={member}
              attachedFiles={attachedFiles}
              apiBaseUrl={API_BASE_URL}
            />
          </div>
        </div>

        <Dialog
          open={openNewTicketModal}
          onClose={() => setOpenNewTicketModal(false)}
          fullWidth
          maxWidth="md"
          PaperProps={{ sx: dialogPaperSx }}
        >
          <DialogTitle sx={{ fontWeight: 700, color: "#223142" }}>
            Create New Ticket
          </DialogTitle>
          <DialogContent dividers>
            <AddWorkOrderSmart
              propertyId={workOrderDetails?.property_id}
              openedByUserId={currentUserId}
              uploadedBy={currentUserId}
              onSuccess={handleNewTicketCreated}
              onClose={() => setOpenNewTicketModal(false)}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={() => setOpenNewTicketModal(false)}
              sx={secondaryButtonSx}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={emailDialogOpen}
          onClose={() => setEmailDialogOpen(false)}
          fullWidth
          maxWidth="sm"
          PaperProps={{ sx: dialogPaperSx }}
        >
          <DialogTitle
            sx={{
              fontWeight: 700,
              fontSize: "1.35rem",
              pt: 3,
              pb: 1,
              color: "#1f1f1f",
            }}
          >
            Send Work Order via Email
          </DialogTitle>

          <DialogContent sx={{ px: 3, pb: 1 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Recipient Email"
              fullWidth
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              type="email"
              sx={textFieldSx}
            />
            <TextField
              margin="dense"
              label="CC Email (optional)"
              fullWidth
              value={ccEmail}
              onChange={(e) => setCcEmail(e.target.value)}
              type="email"
              sx={textFieldSx}
            />
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              pb: 3,
              pt: 1.5,
              justifyContent: "flex-end",
              gap: 1.25,
            }}
          >
            <Button
              onClick={() => setEmailDialogOpen(false)}
              variant="outlined"
              sx={secondaryButtonSx}
            >
              Cancel
            </Button>

            <Button
              onClick={handleSendEmail}
              disabled={isSending || !recipientEmail}
              variant="contained"
              sx={primaryButtonSx}
            >
              {isSending ? "Sending..." : "Send"}
            </Button>
          </DialogActions>
        </Dialog>
      {/* ── Manual Billing Price Dialog ─────────────────────────────────── */}
      <Dialog open={billingDialog.open} onClose={() => setBillingDialog(d => ({ ...d, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>Enter Billing Amounts</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
            {billingDialog.message}
          </Typography>
          <TextField
            label="Labor Amount ($)"
            type="number"
            fullWidth
            value={billingDialog.laborPrice}
            onChange={e => setBillingDialog(d => ({ ...d, laborPrice: e.target.value }))}
            inputProps={{ min: 0, step: "0.01" }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Labor Billing Code"
            fullWidth
            value={billingDialog.timeCode}
            onChange={e => setBillingDialog(d => ({ ...d, timeCode: e.target.value }))}
            placeholder="e.g. SALES4"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Materials Amount ($)"
            type="number"
            fullWidth
            value={billingDialog.materialsPrice}
            onChange={e => setBillingDialog(d => ({ ...d, materialsPrice: e.target.value }))}
            inputProps={{ min: 0, step: "0.01" }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Materials Billing Code"
            fullWidth
            value={billingDialog.materialsCode}
            onChange={e => setBillingDialog(d => ({ ...d, materialsCode: e.target.value }))}
            placeholder="e.g. SALES1"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBillingDialog(d => ({ ...d, open: false }))} sx={{ color: "#6b778c" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleBillingDialogConfirm}
            disabled={billingGenerating}
            startIcon={billingGenerating ? <CircularProgress size={14} color="inherit" /> : <ReceiptLongIcon />}
            sx={{ background: "#2f63ad", "&:hover": { background: "#1e4c8a" } }}
          >
            {billingGenerating ? "Saving…" : "Save Billing Record"}
          </Button>
        </DialogActions>
      </Dialog>

      </Box>
    </LocalizationProvider>
  );
};

export default WorkOrdersDetails;